import { useRef, useState } from 'react';

import { Box, Collapse, IconButton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

// Frosted (translucent + blur) background for sticky footers, so content shows
// through faintly rather than being hard-cut by a solid bar.
const frostedFooter = {
  backgroundColor: (theme: any) =>
    alpha(theme.palette.background.default, 0.72),
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
};

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import StatusBar from '@/designs-dev/device/StatusBar';
import Navigation from '@/karehero-app/shell/Navigation';
import ActionDetail from '@/karehero-app/actions/ActionDetail';
import CreateAction from '@/karehero-app/actions/CreateAction';
import ActionCard, { type Item } from '@/components/wrapped/ActionCard';
import { CALLS } from '@/karehero-app/care/fixtures';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

const SCREEN_PX = 2; // 16px gutter (matches the Figma 343px content width)

// The prototype's today. Pinned rather than `new Date()`: the seed is a fixed
// set of dates, and a list that drifts out of its own week every Monday is a
// prototype that stops being the thing it was drawn as.
const TODAY = new Date('2026-05-20T00:00:00');

const DAY = 86_400_000;

/** The Monday of the week a date falls in. */
const weekStart = (from: Date, offset = 0) => {
  const d = new Date(from);
  // getDay is Sunday-first; the week here starts on Monday.
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + offset * 7);
  d.setHours(0, 0, 0, 0);
  return d;
};

const dayMonth = (d: Date) =>
  `${d.getDate()} ${d.toLocaleDateString('en-GB', { month: 'short' })}`;

/**
 * What the week is called. 'This week' and 'Last week' by name, because that is
 * how somebody refers to the one they are on; anything further out is dates,
 * because 'in four weeks' is arithmetic the reader has to do.
 */
const weekLabel = (offset: number, start: Date) => {
  if (offset === 0) return 'This week';
  if (offset === -1) return 'Last week';
  if (offset === 1) return 'Next week';
  const end = new Date(start.getTime() + 6 * DAY);
  return `${dayMonth(start)} – ${dayMonth(end)}`;
};

const LINKED_CALL = {
  when: '7th May at 10:00',
  withName: 'Yasmin',
  from: 'Yasmin',
};

const SEED: Item[] = [
  {
    id: 1,
    date: '2026-05-15',
    title: 'Upload your bank statement',
    overdue: true,
    schedule: 'Due Fri 15th May · 16:00',
    who: 'OP',
    regarding: 'Regarding Mum’s care',
    createdBy: 'Abbie',
    assignedTo: 'Assigned to you',
    due: 'Due Fri 15th May · 16:00',
    note: 'We need three months of recent statements to complete the funding picture. Please upload these as a PDF. I’ve attached an example of what they should look like.',
    linkedCall: LINKED_CALL,
    files: ['example_file.pdf'],
    kind: 'cam',
    done: false,
  },
  {
    id: 2,
    date: '2026-05-18',
    title: 'Pick up prescription',
    overdue: true,
    schedule: 'Due Mon 18th May · 09:00',
    who: 'OP',
    createdBy: 'Abbie',
    due: 'Due Mon 18th May · 09:00',
    linkedCall: LINKED_CALL,
    files: ['example_file.pdf', 'example_file.pdf'],
    kind: 'user',
    done: false,
  },
  {
    id: 3,
    date: '2026-05-22',
    title: 'Buy a pill box',
    schedule: 'Due Fri 22nd May · 16:00',
    who: 'OP',
    regarding: 'Regarding Mum’s care',
    createdBy: 'Abbie',
    assignedTo: 'Assigned to you',
    due: 'Due Fri 22nd May · 16:00',
    linkedCall: LINKED_CALL,
    files: ['example_file.pdf', 'example_file.pdf'],
    kind: 'user',
    done: false,
  },
  {
    id: 4,
    date: '2026-05-26',
    title: 'Order incontinence pads',
    schedule: 'Due Tue 26th May · 09:00',
    who: 'OP',
    regarding: 'Regarding Mum’s care',
    createdBy: 'Abbie',
    assignedTo: 'Assigned to you',
    due: 'Due Tue 26th May · 09:00',
    note: 'I’ve set this to repeat weekly so you don’t run low. Adjust anytime.',
    linkedCall: LINKED_CALL,
    files: ['example_file.pdf'],
    kind: 'cam',
    done: false,
  },
  // A repeating one, set by a care expert. Members can set their own from the
  // create sheet too; this is the other source, and with nothing repeating in
  // the seed the places that draw a repeat were unreachable.
  {
    id: 7,
    date: '2026-05-23',
    title: 'Weigh Mum and log it',
    schedule: 'Repeats every Saturday at 16:00',
    repeats: 'Repeats every Saturday at 16:00',
    who: 'OP',
    regarding: 'Regarding Mum’s care',
    createdBy: 'Abbie',
    assignedTo: 'Assigned to you',
    note: 'Weekly, so we can see any change before it becomes a problem.',
    linkedCall: LINKED_CALL,
    files: [],
    kind: 'cam',
    done: false,
  },
  // A repeating one the member set themselves. Edit only shows on actions you
  // created, so with a care expert's repeat as the only one in the seed the
  // chooser and the series form were built and unreachable.
  {
    id: 8,
    date: '2026-05-25',
    title: 'Order Mum’s repeat prescription',
    schedule: 'Repeats every 4 weeks on Monday at 09:00',
    repeats: 'Repeats every 4 weeks on Monday at 09:00',
    who: 'OP',
    regarding: 'Regarding Mum’s care',
    createdBy: 'Abbie',
    assignedTo: 'Assigned to you',
    // Carried even though a repeat reads its schedule rather than a due date:
    // the series form puts it in 'Effective from', and the care expert's
    // repeat has none, so that field opens blank on action 7.
    due: 'Due Mon 25th May · 09:00',
    files: [],
    kind: 'user',
    done: false,
  },
  // Two finished, so the Done section is there to open. Without them the seed
  // was four open actions and a section that only existed once you ticked
  // something off, which is not a state anybody looking at this would find.
  {
    id: 5,
    date: '2026-05-19',
    title: 'Send Mum’s GP details',
    schedule: 'Due Tue 19th May · 12:00',
    who: 'OP',
    regarding: 'Regarding Mum’s care',
    createdBy: 'Abbie',
    assignedTo: 'Assigned to you',
    due: 'Due Tue 19th May · 12:00',
    linkedCall: LINKED_CALL,
    files: [],
    kind: 'cam',
    done: true,
  },
  {
    id: 6,
    date: '2026-05-14',
    title: 'Book the hospital transport',
    schedule: 'Due Thu 14th May · 09:30',
    who: 'OP',
    createdBy: 'Abbie',
    due: 'Due Thu 14th May · 09:30',
    files: [],
    kind: 'user',
    done: true,
  },
];

const ActionsScreen = ({
  initial,
  onNotifications,
  title,
  avatarInitials = 'OP',
  account,
  create = 'footer',
  createOpen,
  onCreateOpenChange,
}: {
  /**
   * Name beside the logo. Omitted in the app, where the tab is about you and the
   * header has nothing to add; a flow that is about one person passes theirs, so
   * the bar says who the list is for.
   */
  title?: string;
  /** Whose account this is. Defaults to the app's own seed. */
  avatarInitials?: string;
  /** Makes the avatar open the settings sheet. See Navigation's `account`. */
  account?: Parameters<typeof Navigation>[0]['account'];
  /** Jump-state for handover minis and stories. */
  initial?: {
    /** Opens the Done section, for a mini that is about finished work. */
    doneOpen?: boolean;
    /** Which week to open on, as an offset: -1 is last week, 1 is next. */
    week?: number;
    detailId?: number;
    creating?: boolean;
    /** Opens the create sheet with the repeat schedule already expanded. */
    creatingRepeats?: boolean;
    empty?: boolean;
    /**
     * Stand in a different action set for the seeded one — for explorations
     * that need this screen showing their own content rather than the stock
     * seed. Same idea as `empty`, which already swaps the list wholesale.
     */
    actions?: Item[];
    /**
     * Drop the All / To do / Done segments. For frames showing the list as a
     * destination — one action that just arrived — where filtering a list of
     * one is noise.
     */
    editing?: boolean;
    editingSeries?: boolean;
    deleting?: boolean;
  };
  /** Bell tap — opens the notification centre. Omitted in stories/handover. */
  onNotifications?: () => void;
  /**
   * Create action as a full-width button in a frosted footer, as main has it,
   * or 'none' where the button lives somewhere else, like the Care hub's tab
   * bar, which opens the sheet through `createOpen`.
   */
  create?: 'footer' | 'none';
  /** The create sheet, held outside, for a button that lives outside. */
  createOpen?: boolean;
  onCreateOpenChange?: (open: boolean) => void;
} = {}) => {
  const seed = initial?.empty ? [] : (initial?.actions ?? SEED);
  const [actions, setActions] = useState<Item[]>(seed);
  // Done is a section rather than a filter: a pill bar made you choose between
  // two lists when one of them is three finished things you glance at. It sits
  // under the open work, collapsed, the way the CAM's actions tab has it.
  const [doneOpen, setDoneOpen] = useState(Boolean(initial?.doneOpen));
  // Which week is on screen, as an offset from the one TODAY is in.
  const [week, setWeek] = useState(initial?.week ?? 0);
  // Where a drag started, for the swipe between weeks.
  const swipeFrom = useRef<number | null>(null);
  const [detail, setDetail] = useState<Item | null>(
    seed.find((a) => a.id === initial?.detailId) ?? null,
  );
  const [ownCreating, setOwnCreating] = useState(
    Boolean(initial?.creating || initial?.creatingRepeats),
  );
  const creating = createOpen ?? ownCreating;
  const setCreating = onCreateOpenChange ?? setOwnCreating;

  const setDone = (id: number, done: boolean) =>
    setActions((prev) => prev.map((a) => (a.id === id ? { ...a, done } : a)));
  const toggle = (id: number) =>
    setActions((prev) =>
      prev.map((a) => (a.id === id ? { ...a, done: !a.done } : a)),
    );

  const start = weekStart(TODAY, week);
  const end = new Date(start.getTime() + 7 * DAY);
  const inWeek = (a: Item) => {
    if (!a.date) return week === 0; // undated work belongs to now
    const d = new Date(`${a.date}T00:00:00`);
    return d >= start && d < end;
  };

  // Overdue only on the week you are actually in. On a past week everything is
  // already behind; on a future one nothing can be late yet. Pinning it to
  // every week would put the same red rows above four different lists.
  const overdue = week === 0 ? actions.filter((a) => !a.done && a.overdue) : [];
  const open = actions.filter(
    (a) => !a.done && !(week === 0 && a.overdue) && inWeek(a),
  );
  const done = actions.filter((a) => a.done && inWeek(a));
  const noneAtAll = actions.length === 0;

  return (
    <ScreenRoot relative>
      <StatusBar />

      <Navigation
        title={title}
        onNotifications={onNotifications}
        avatarInitials={avatarInitials}
        account={account}
      />

      {noneAtAll ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: SCREEN_PX,
          }}
        >
          <Box sx={{ mb: 3 }}>
            <PageHeader title='No actions yet' isCentered />
          </Box>
          <Box sx={{ width: '100%' }}>
            <Button
              variant='primary'
              size='lg'
              isFullWidth
              ariaLabel='Create action'
              onPress={() => setCreating(true)}
            >
              Create action
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          {/* The week you are on, and the way to the ones either side. Arrows
              as well as the swipe: a swipe nobody knows is there is not a way
              anywhere, and this is read on a screen you cannot swipe. */}
          <Stack
            sx={{
              px: SCREEN_PX,
              pt: 2,
              flexShrink: 0,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
            direction='row'
          >
            <IconButton
              aria-label='Previous week'
              size='small'
              onClick={() => setWeek((w) => w - 1)}
            >
              <Icon icon='chevron-left' fontSize={14} />
            </IconButton>
            <Typography variant='h6'>{weekLabel(week, start)}</Typography>
            <IconButton
              aria-label='Next week'
              size='small'
              onClick={() => setWeek((w) => w + 1)}
            >
              <Icon icon='chevron-right' fontSize={14} />
            </IconButton>
          </Stack>

          <Box
            sx={{ flex: 1, overflow: 'auto', px: SCREEN_PX, pt: 2, pb: '96px' }}
            onPointerDown={(e) => {
              swipeFrom.current = e.clientX;
            }}
            onPointerUp={(e) => {
              const from = swipeFrom.current;
              swipeFrom.current = null;
              if (from === null) return;
              const moved = e.clientX - from;
              // Far enough to be a swipe rather than a tap that wandered.
              if (Math.abs(moved) < 60) return;
              setWeek((w) => w + (moved < 0 ? 1 : -1));
            }}
          >
            <Stack spacing={1}>
              {/* Overdue first, then the week. The row says it is overdue
                  itself, in red, so nothing needs a heading over it. */}
              <Stack spacing={1.5}>
                {[...overdue, ...open].map((a) => (
                  <ActionCard
                    key={a.id}
                    action={a}
                    onToggle={toggle}
                    onOpen={setDetail}
                  />
                ))}
              </Stack>

              {overdue.length + open.length === 0 && (
                <Typography
                  sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}
                  variant='body1'
                >
                  Nothing due {week === 0 ? 'this week' : 'that week'}
                </Typography>
              )}

              {/* Done at the bottom, collapsed, with a count — the same shape
                  the CAM's actions tab uses, so a finished action looks the
                  same wherever it is seen. */}
              {done.length > 0 && (
                <Box sx={{ pt: 1 }}>
                  <Stack
                    onClick={() => setDoneOpen((o) => !o)}
                    sx={{
                      alignItems: 'center',
                      cursor: 'pointer',
                      width: 'fit-content',
                      color: 'text.secondary',
                    }}
                    direction='row'
                    spacing={0.5}
                  >
                    <Icon
                      icon={doneOpen ? 'chevron-down' : 'chevron-right'}
                      fontSize={12}
                    />
                    <Typography
                      variant='overline'
                      sx={{ display: 'block', color: 'text.secondary' }}
                    >
                      Done · {done.length}
                    </Typography>
                  </Stack>
                  <Collapse in={doneOpen}>
                    <Stack sx={{ pt: 1 }} spacing={1.5}>
                      {done.map((a) => (
                        <ActionCard
                          key={a.id}
                          action={a}
                          onToggle={toggle}
                          onOpen={setDetail}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </Box>
              )}
            </Stack>
          </Box>
          {create === 'footer' && (
            <Box
              sx={[
                {
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  px: SCREEN_PX,
                  py: 1.5,
                },
                frostedFooter,
              ]}
            >
              <Button
                variant='primary'
                size='lg'
                isFullWidth
                ariaLabel='Create action'
                onPress={() => setCreating(true)}
              >
                Create action
              </Button>
            </Box>
          )}
        </>
      )}

      {detail && (
        <ActionDetail
          action={detail}
          onClose={() => setDetail(null)}
          onSetDone={setDone}
          onDelete={(id) =>
            setActions((prev) => prev.filter((a) => a.id !== id))
          }
          resolveLinkedCall={(linked) =>
            CALLS.find((c) => c.when === linked.when) ?? null
          }
          initialEdit={Boolean(initial?.editing)}
          initialEditSeries={Boolean(initial?.editingSeries)}
          initialDelete={Boolean(initial?.deleting)}
        />
      )}
      {creating && (
        <CreateAction
          initialRepeats={Boolean(initial?.creatingRepeats)}
          onClose={() => setCreating(false)}
        />
      )}
    </ScreenRoot>
  );
};

export default ActionsScreen;
